import { en, type Messages } from './en';
import { hi } from './hi';

export { en, hi };
export type { Messages };

export const LANGUAGES = ['en', 'hi'] as const;
export type Lang = (typeof LANGUAGES)[number];
export const LANGUAGE_NAMES: Record<Lang, string> = { en: 'English', hi: 'हिंदी' };

const CATALOGUES: Record<Lang, Messages> = { en, hi };

type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

/** Every translatable key, e.g. 'floor.todo' or 'gateStatus.blocked.paint'. */
export type MessageKey = Leaves<typeof en>;
export type Params = Record<string, string | number>;
export type Translate = (key: MessageKey, params?: Params) => string;

function lookup(cat: unknown, key: string): string | undefined {
  let cur: unknown = cat;
  for (const part of key.split('.')) {
    if (cur && typeof cur === 'object' && part in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[part];
    } else {
      // Keys may themselves contain dots (e.g. timeline['plan.publish']).
      const rest = key.slice(key.indexOf(part));
      if (cur && typeof cur === 'object' && rest in (cur as Record<string, unknown>)) {
        return (cur as Record<string, string>)[rest];
      }
      return undefined;
    }
  }
  return typeof cur === 'string' ? cur : undefined;
}

export function interpolate(s: string, params?: Params): string {
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m));
}

/** Build a translator. Missing keys fall back to English, then to the key itself. */
export function createTranslator(lang: Lang): Translate {
  const cat = CATALOGUES[lang] ?? en;
  return (key, params) => interpolate(lookup(cat, key) ?? lookup(en, key) ?? key, params);
}

/** Indian digit grouping (12,000 · 1,20,000), Latin digits in both languages for the floor. */
const nf = new Intl.NumberFormat('en-IN');
export const formatNumber = (n: number): string => nf.format(n);
